if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.setHeaderCartIconAccessibility();
    }

    init() {
      if (!this.isInitialized) {
        window.loadTemplateContent(this);

        requestAnimationFrame(() => {
          // Bind various things
          this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.moveCssLinks();
          this.isInitialized = true;
        });
      }
    }

    reload() {
      const sections = this.getSectionsToRender().map((section) => section.id);
      fetch(`?sections=${sections}`)
        .then((response) => response.json())
        .then((response) => {
          this.renderContents({
            sections: {
              ...response
            }
          }, true);
        })
        .catch((e) => {
          console.error(e);
        });
    }

    moveCssLinks() {
      const cssLinks = this.querySelectorAll('link[rel="stylesheet"][href*=".css"]');
      cssLinks.forEach(link => {
        // Check if the link already exists in the document head
        const href = link.href;
        if (!document.head.querySelector(`link[href="${href}"]`)) {
          // Clone the link element
          const newLink = link.cloneNode(true);
          // Append the cloned link to the document head
          document.head.appendChild(newLink);
        }
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      this.init();

      if (triggeredBy) this.setActiveElement(triggeredBy);
      // MFO (site fixes J-09): opened without a trigger (for example from the add-to-cart notification's "View cart"),
      // closing returns focus to whatever had it, instead of dropping it to the page.
      else if (document.activeElement && document.activeElement !== document.body) this.setActiveElement(document.activeElement);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.removeDeleteTabIndex();

      // MFO (site fixes J-09): move focus into the drawer once per open, whether or not the slide-in
      // transition runs. With "reduce motion" the drawer has no transition, so transitionend never fired
      // and focus stayed behind the dialog; now it's trapped right away, and otherwise on transitionend
      // with a 400 ms fallback.
      this.cancelOpenFocus();
      const onOpened = () => {
        this.cancelOpenFocus();
        this.trapDrawerFocus();
        publish(PUB_SUB_EVENTS.cartOpen);
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // A 0 ms timer runs after the one above that adds .active, so the drawer is visible by then.
        this.openFocusTimer = setTimeout(onOpened);
      } else {
        this.openFocusListener = onOpened;
        this.addEventListener('transitionend', onOpened, {once: true});
        this.openFocusTimer = setTimeout(onOpened, 400);
      }

      document.body.classList.add('overflow-hidden');
    }

    // MFO (site fixes J-09): drop a pending focus move (the drawer closed, or opened again, first).
    cancelOpenFocus() {
      clearTimeout(this.openFocusTimer);
      if (this.openFocusListener) this.removeEventListener('transitionend', this.openFocusListener);
      this.openFocusListener = null;
    }

    // MFO (site fixes J-09): the dialog panel is both the trap container and the first focus, so the
    // trap's Shift+Tab handler is armed on it (with #CartDrawer as the container it never was, and
    // Shift+Tab left the dialog). The last stop is the last control that's displayed: the empty cart
    // hides the header and footer.
    trapDrawerFocus() {
      const panel = this.querySelector('.drawer__inner');
      if (!panel) return;
      const shown = getFocusableElements(panel).filter(
        (element) => element.offsetWidth || element.offsetHeight || element.getClientRects().length
      );
      trapFocus(panel, panel, shown[shown.length - 1]);
    }

    close() {
      this.cancelOpenFocus(); // MFO (site fixes J-09)
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      publish(PUB_SUB_EVENTS.cartClose);
    }

    /**
     * Ensure all remove buttons can be tabbed to
     */
    removeDeleteTabIndex() {
      this.querySelectorAll('.cart-remove-button').forEach((button) => {
        button.removeAttribute('tabindex')
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState, preventOpen = false) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      setTimeout(() => {
        window.loadTemplateContent(this);
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      });

      this.removeDeleteTabIndex();

      if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen) {
        setTimeout(() => {
          this.open();
        });
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      html = html.replace(/<template\b[^>]*>|<\/template>/g, '');
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.whenDefined('cart-items').then(() => {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          // MFO (site fixes J-20): write "New estimated total: …" into the drawer's status region, which
          // cart.js keeps in place while the rest of the panel is re-rendered.
          {
            id: 'CartDrawer-LiveRegionText',
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  });
}
